import { useCallback, useEffect, useRef, useState } from 'react';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import { Device } from '../types';

/**
 * Plan 94 Phase 5 — resolve a single device by RDS id via GET /devices/:id
 * (scope-checked server-side). The device workspace uses this instead of pulling
 * the whole fleet with useDevices(), so it scales past a dropdown's worth of
 * devices. A 404 (bad id or out-of-scope) surfaces as notFound.
 */
export function useDevice(deviceId: string | undefined) {
  const [device, setDevice] = useState<Device | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // FS-502: a write to the homeowner's Firestore records reaches the console's
  // projection through the sync queue, so the device on screen is briefly the
  // pre-edit one. Bumping this re-runs the fetch without remounting the tab.
  const [reloadCount, setReloadCount] = useState(0);
  const refetch = useCallback(() => setReloadCount((n) => n + 1), []);
  // A refresh of the device already on screen must not throw the page back to
  // its loading shell — that would unmount whatever the operator has open.
  const loadedIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    if (!deviceId) {
      setLoading(false);
      setNotFound(true);
      return;
    }
    setLoading(loadedIdRef.current !== deviceId);
    setNotFound(false);
    setError(null);
    axiosInstance
      .get<Device>(`${config.devicesApiUrl}/devices/${encodeURIComponent(deviceId)}`)
      .then((res) => {
        if (cancelled) return;
        loadedIdRef.current = deviceId;
        setDevice(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setNotFound(true);
        } else {
          console.error('Failed to load device:', err);
          setError('Failed to load device');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [deviceId, reloadCount]);

  return { device, loading, notFound, error, refetch };
}
